import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Check, ArrowRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { packages, fmtPrice } from '../../data/webServicesData';
import AnimateIn from '../AnimateIn';

const POSITIONS = {
  0: { x: '0%', scale: 1, opacity: 1, zIndex: 30 },
  1: { x: '72%', scale: 0.78, opacity: 0.4, zIndex: 20 },
  2: { x: '0%', scale: 0.6, opacity: 0, zIndex: 0 },
  3: { x: '-72%', scale: 0.78, opacity: 0.4, zIndex: 20 },
};

const PackageCarousel = () => {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [index, setIndex] = useState(1);

  const prev = () => setIndex((i) => (i + 3) % 4);
  const next = () => setIndex((i) => (i + 1) % 4);

  const requestContact = (pkg) => {
    const price = `${fmtPrice(pkg.price, language)}${pkg.startingAt ? '+' : ''}`;
    const msg =
      language === 'fr'
        ? `Bonjour, je suis intéressé par le forfait ${pkg.name.fr} (${price}). J'aimerais discuter de mon projet de site web.`
        : `Hi, I'm interested in the ${pkg.name.en} package (${price}). I'd like to discuss my website project.`;
    navigate('/contact', { state: { prefill: msg } });
  };

  return (
    <section id="ws-packages" className="py-20 md:py-28 scroll-mt-20 overflow-hidden" data-testid="package-carousel-section">
      <AnimateIn className="text-center mb-14 px-6">
        <p className="accent-tag justify-center">{t('carouselTag')}</p>
        <h2
          className="text-3xl md:text-4xl font-bold tracking-[-0.02em] text-white"
          style={{ fontFamily: "'Syne', sans-serif" }}
          data-testid="carousel-title"
        >
          {t('carouselTitle')}
        </h2>
      </AnimateIn>

      <div className="relative h-[640px] md:h-[620px]" data-testid="package-carousel">
        {packages.map((pkg, i) => {
          const offset = (i - index + 4) % 4;
          const pos = POSITIONS[offset];
          const isCenter = offset === 0;
          return (
            <motion.div
              key={pkg.id}
              className="absolute top-0 left-1/2 w-[min(680px,90vw)] h-full"
              style={{ marginLeft: 'max(-340px, -45vw)' }}
              animate={pos}
              transition={{ type: 'spring', stiffness: 220, damping: 30 }}
              drag={isCenter ? 'x' : false}
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.15}
              onDragEnd={(e, info) => {
                if (info.offset.x < -80) next();
                else if (info.offset.x > 80) prev();
              }}
              data-testid={`carousel-card-${pkg.id}`}
            >
              <div
                className={`relative h-full rounded-3xl overflow-hidden border transition-shadow duration-500 ${
                  isCenter ? '' : 'cursor-pointer'
                }`}
                style={{
                  borderColor: isCenter ? `${pkg.color}55` : 'rgba(255,255,255,0.08)',
                  boxShadow: isCenter ? `0 30px 80px ${pkg.color}22` : 'none',
                }}
                onClick={() => !isCenter && setIndex(i)}
              >
                <img src={pkg.wallpaper} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" draggable={false} />
                <div
                  className="absolute inset-0"
                  style={{ background: 'linear-gradient(180deg, rgba(3,3,3,0.55) 0%, rgba(3,3,3,0.72) 45%, rgba(3,3,3,0.97) 100%)' }}
                />

                {pkg.popular && isCenter && (
                  <span
                    className="absolute top-5 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-[11px] font-bold tracking-[0.15em] uppercase text-black"
                    style={{ background: `linear-gradient(135deg, ${pkg.color}, #fff3d6)` }}
                    data-testid="carousel-popular-badge"
                  >
                    {t('wsMostPopular')}
                  </span>
                )}

                <div className="absolute inset-x-0 bottom-0 p-7 md:p-10">
                  <p className="text-xs font-semibold tracking-[0.2em] uppercase mb-2" style={{ color: pkg.color }}>
                    {pkg.tagline[language]}
                  </p>
                  <h3 className="text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: "'Syne', sans-serif" }}>
                    {pkg.name[language]}
                  </h3>
                  <p className="text-2xl md:text-[2rem] font-bold mb-5" style={{ fontFamily: "'Syne', sans-serif", color: pkg.color }}>
                    {pkg.startingAt && <span className="text-sm font-medium text-gray-400 mr-2">{t('wsStartingAt')}</span>}
                    {fmtPrice(pkg.price, language)}
                    {pkg.startingAt && '+'}
                  </p>

                  {isCenter && (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 mb-7">
                        {pkg.features[language].slice(0, 8).map((feat, fi) => (
                          <div key={fi} className="flex items-start gap-2">
                            <Check size={13} strokeWidth={3} className="mt-1 shrink-0" style={{ color: pkg.color }} />
                            <span className="text-[13px] text-gray-300 leading-snug">{feat}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex flex-wrap items-center gap-4">
                        <button
                          onClick={() => navigate(`/services/customize?pkg=${pkg.id}`)}
                          className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold text-black transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(255,255,255,0.15)]"
                          style={{ background: `linear-gradient(135deg, ${pkg.color}, #ffffff)` }}
                          data-testid={`carousel-select-${pkg.id}`}
                        >
                          {t('carouselSelect')}
                          <ArrowRight size={15} />
                        </button>
                        <button
                          onClick={() => requestContact(pkg)}
                          className="text-[13px] text-gray-400 underline underline-offset-4 hover:text-white transition-colors"
                          data-testid={`carousel-contact-${pkg.id}`}
                        >
                          {t('carouselContact')}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}

        <button
          onClick={prev}
          className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 z-40 w-12 h-12 rounded-full flex items-center justify-center border border-white/20 bg-black/50 backdrop-blur-md text-white transition-all hover:border-white/60 hover:scale-110"
          aria-label="Previous package"
          data-testid="carousel-prev"
        >
          <ChevronLeft size={22} />
        </button>
        <button
          onClick={next}
          className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 z-40 w-12 h-12 rounded-full flex items-center justify-center border border-white/20 bg-black/50 backdrop-blur-md text-white transition-all hover:border-white/60 hover:scale-110"
          aria-label="Next package"
          data-testid="carousel-next"
        >
          <ChevronRight size={22} />
        </button>
      </div>

      <div className="flex justify-center gap-2.5 mt-8" data-testid="carousel-dots">
        {packages.map((pkg, i) => (
          <button
            key={pkg.id}
            onClick={() => setIndex(i)}
            className="h-2 rounded-full transition-all duration-300"
            style={{
              width: i === index ? '28px' : '8px',
              background: i === index ? pkg.color : 'rgba(255,255,255,0.2)',
            }}
            aria-label={pkg.name[language]}
            data-testid={`carousel-dot-${pkg.id}`}
          />
        ))}
      </div>
    </section>
  );
};

export default PackageCarousel;
